/**
 * Graph legend overlay — small floating panel showing each category color
 * present in the current graph with a count of nodes of that category.
 *
 * Helps an analyst reading a dense graph (an attack-surface picture with 50+
 * nodes) understand at a glance "blue = network, purple = identity, …"
 * without having to mouse over every node.
 *
 * Collapsible to a single icon so it doesn't eat canvas real estate on small
 * graphs. Defaults to open the first time the graph has ≥3 distinct
 * categories — for tiny graphs the legend is just noise.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORY_COLORS, getCategoryForType, type Category } from '@cade/graph-logic';
import type { ElementDefinition } from 'cytoscape';

const CATEGORY_ORDER: Category[] = [
  'identity',
  'compute',
  'network',
  'application',
  'data',
  'findings',
  'context',
  'framework',
];

const CATEGORY_LABELS: Record<Category, string> = {
  identity:    'Identity',
  compute:     'Compute',
  network:     'Network',
  application: 'Application',
  data:        'Data',
  findings:    'Findings',
  context:     'Context',
  framework:   'Framework',
};

interface Props {
  elements: ElementDefinition[];
  isDark?: boolean;
}

export function GraphLegend({ elements, isDark }: Props) {
  // Per-category node counts on the visible element set.
  const categoryCounts = useMemo(() => {
    const counts = new Map<Category, number>();
    for (const el of elements) {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (!isNode) continue;
      const cat = getCategoryForType(el.data.type as string | undefined);
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    }
    return counts;
  }, [elements]);

  const present = CATEGORY_ORDER.filter((c) => categoryCounts.has(c));
  const autoOpen = present.length >= 3;
  const [open, setOpen] = useState(autoOpen);
  // Once the user manually collapses or expands, that intent sticks — we
  // don't pop the legend back open just because a streaming AI response
  // pushed the category count past the threshold. The earlier implementation
  // re-fired `setOpen(autoOpen)` on every threshold flip, which silently
  // overrode the user's collapse.
  const userTouchedRef = useRef(false);
  useEffect(() => {
    if (userTouchedRef.current) return;
    setOpen(autoOpen);
  }, [autoOpen]);
  const setOpenManually = useCallback((next: boolean) => {
    userTouchedRef.current = true;
    setOpen(next);
  }, []);

  if (present.length === 0) return null;

  const dark = isDark ?? (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');

  return (
    <div className={`graph-legend ${open ? 'is-open' : 'is-collapsed'}`}>
      {open ? (
        <>
          <div className="graph-legend-header">
            <span>Legend</span>
            <button
              className="graph-legend-toggle"
              onClick={() => setOpenManually(false)}
              title="Collapse legend"
              aria-label="Collapse legend"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          <ul className="graph-legend-list">
            {present.map((cat) => {
              const colors = CATEGORY_COLORS[cat];
              const fill = dark ? colors.fill.dark : colors.fill.light;
              const stroke = dark ? colors.stroke.dark : colors.stroke.light;
              return (
                <li key={cat} className="graph-legend-row">
                  <span
                    className="graph-legend-swatch"
                    style={{ backgroundColor: fill, borderColor: stroke }}
                    aria-hidden="true"
                  />
                  <span className="graph-legend-label">{CATEGORY_LABELS[cat]}</span>
                  <span className="graph-legend-count">{categoryCounts.get(cat)}</span>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <button
          className="graph-legend-collapsed-btn"
          onClick={() => setOpenManually(true)}
          title="Show legend"
          aria-label="Show legend"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
            <line x1="9" y1="15" x2="9.01" y2="15" />
            <line x1="15" y1="15" x2="15.01" y2="15" />
          </svg>
        </button>
      )}
    </div>
  );
}
